import * as React from "react";
import { observer } from "mobx-react-lite";
import { Link } from "react-router-dom";
import { IdentityDraftNetworkResourceMobx } from "../../../mst/stores/identity.draft.networkresource";
import { ConsoleContext } from "../../../mobxStores/consoleContext/consoleContext";
import { IPorPort } from "../../../components/networkResources/ipOrPort";
import { Tooltip } from "../../../components/Tooltip";
import { InfoTooltip } from "../../../components/InfoTooltip";
import { AgentInfo } from "../../../schema/types/agent";
import moment from "moment-timezone";
import { dateString } from "../../../components/dateStringFn";
import { formatStrings } from "../../../utils/time";
import { maxBy } from "lodash";

interface Props {
  type: "fqdn" | "ip";
  resource: IdentityDraftNetworkResourceMobx;
  isViewResolverIP: boolean;
  agentInfos?: AgentInfo[];
}

const NetworkResourceTableItemRaw: React.FC<Props> = ({ type, resource, isViewResolverIP, agentInfos }) => {
  const { org } = ConsoleContext;

  const getLastActiveText = (infos?: AgentInfo[]) => {
    try {
      if (!infos || infos.length === 0) return "Never";
      const mostRecent = maxBy(infos, (info) => {
        if (!info.lastActive || info.lastActive.length === 0) return 0;
        return moment(info.lastActive).valueOf();
      });
      if (mostRecent?.lastActive && mostRecent.lastActive.length > 0) {
        return dateString(mostRecent.lastActive, { format: formatStrings.humanizeWAgo });
      }
      return "Never";
    } catch (e) {
      return "Never";
    }
  };

  const getIsActive = (infos?: AgentInfo[]) => {
    if (!infos || infos.length === 0) return false;
    try {
      const mostRecent = maxBy(infos, (info) => {
        if (!info.lastActive || info.lastActive.length === 0) return 0;
        return moment(info.lastActive).valueOf();
      });
      if (!mostRecent?.lastActive) return false;
      const lastActiveDate = moment(mostRecent.lastActive);
      const nowDate = moment();
      const diff = nowDate.diff(lastActiveDate, "seconds");
      return diff <= 60;
    } catch (e) {
      return false;
    }
  };

  const getEnvText = (infos?: AgentInfo[]) => {
    if (infos && infos.length > 0) {
      return JSON.stringify(infos, null, 2);
    }
    return "Details not available";
  };

  const lastActiveText = getLastActiveText(agentInfos);
  const isActive = getIsActive(agentInfos);
  const envText = getEnvText(agentInfos);

  return (
    <>
      <div className="w-3/12 pr-2">
        <Tooltip title={resource.resourceName} open={resource.resourceName.length > 16}>
          <div className="truncate pr-2">{resource.resourceName}</div>
        </Tooltip>
      </div>
      <div
        className={`w-${
          type === "fqdn" ? (isViewResolverIP ? "2/12" : "3/12") : "3/12"
        } flex flex-col items-start pr-2`}
      >
        <Link className={`truncate ngfocus color-link`} to={`/console/org/${org}/agent/${resource.agentName}`}>
          {resource.agentName}
        </Link>
        {agentInfos && agentInfos.some((info) => info.lastActive && info.lastActive.length > 0) ? (
          <div className={`${isActive ? "color-action" : "color-danger"} text-sm flex`}>
            <span>
              {lastActiveText === "Now" ? "" : "Last Active "}
              {lastActiveText}
            </span>
            <InfoTooltip style={{ marginLeft: 4 }} title={envText} />
          </div>
        ) : agentInfos && agentInfos.length > 0 ? (
          <span className={`text-sm color-danger`}>Was Never Active</span>
        ) : (
          <span className={`text-sm color-muted`}>Checking...</span>
        )}
      </div>
      {type === "fqdn" ? (
        <div className={`w-${isViewResolverIP ? "2/12" : "3/12"} pr-2`}>
          <Tooltip title={resource.fqdnInput.value} open={resource.fqdnInput.value.length > 16}>
            <div className="truncate pr-2">{resource.fqdnInput.value}</div>
          </Tooltip>
        </div>
      ) : null}
      {type === "ip" ? (
        <div className="w-3/12 flex flex-wrap items-start pr-2 gap-1">
          {resource.IPs.map((ip) => (
            <IPorPort item={ip} />
          ))}
        </div>
      ) : null}
      {type === "fqdn" && isViewResolverIP ? <div className="w-2/12">{resource.resolverIpInput.value}</div> : null}
      <div className={`${type === "fqdn" ? "w-2/12" : "w-2/12"} flex flex-wrap items-start pr-2 gap-1`}>
        {resource.ports.map((port) => (
          <IPorPort item={port} />
        ))}
      </div>
    </>
  );
};

export const NetworkResourceTableItem = observer(NetworkResourceTableItemRaw);
